import { AxiosResponse } from 'axios';
import {
  SET_EMPLOYEE_OVERTIMES,
  SET_EMPLOYEE_OVERTIME,
  SET_ERRORMSG,
  SET_EMPLOYEE_OVERTIMES_BY_EMPLOYEE,
  SET_OVERTIME_ANALYTICS,
} from '@/store/mutation_types';
import {
  CLEAR_EMPLOYEE_OVERTIME_ISLOADING,
  DELETE_EMPLOYEE_OVERTIME,
  EDIT_EMPLOYEE_OVERTIME,
  EXPORT_OVERTIME,
  GET_EMPLOYEE_OVERTIMES,
  GET_EMPLOYEE_OVERTIME,
  GET_EMPLOYEE_OVERTIMES_BY_EMPLOYEE,
  GET_OVERTIME_ANALYTICS,
  REGISTER_EMPLOYEE_OVERTIME,
  UPDATE_OVERTIME_STATUS,
} from '@/store/action_types';
import EmployeeOvertime from '@/services/employee_overtime';
import { Module } from 'vuex';
import {
  EditEmployeeOvertime,
  ExportOvertime,
  GetEmployeeOvertime,
  GetOvertimeAnalytics,
  RegisterEmployeeOvertime,
  UpdateOvertimeStatus,
} from '@/dtos/employee_overtime.dto';

/**
 * The axios interceptor already pushes a human message into the error store for
 * 4xx/5xx and then resolves the promise with `undefined`. Committing
 * `result?.data?.msg` unconditionally therefore overwrote that message with
 * undefined, which is why payroll-lock (409) and validation (400) errors never
 * reached the user. Only commit when there is actually something to say.
 */
const reportError = (commit: any, result: any) => {
  const msg = result?.data?.msg;
  if (msg) commit(SET_ERRORMSG, msg);
};

/**
 * Every list here is replaced wholesale on each load, including with `null` while a
 * request is in flight. The previous module skipped the write whenever `data` was
 * null, so switching month left the OLD month's rows on screen under the NEW
 * month's heading, and a failed request left them there permanently.
 *
 * Each list also carries a `requestId`; a response is only applied when it is the
 * most recent request for that list, so flipping quickly between months can no
 * longer land an out-of-order response.
 */
interface OvertimeState {
  data: any;
  isLoading: boolean;
  meta?: any;
  requestId: number;
}

const emptyState = (): OvertimeState => ({ data: null, isLoading: false, meta: null, requestId: 0 });

const applyMutation = (slot: OvertimeState, payload: any) => {
  if (payload.requestId !== undefined && payload.requestId < slot.requestId) return;
  if (payload.requestId !== undefined) slot.requestId = payload.requestId;
  if ('data' in payload) slot.data = payload.data;
  if ('meta' in payload) slot.meta = payload.meta;
  slot.isLoading = Boolean(payload.isLoading);
};

const employeeOvertime: Module<any, {}> = {
  state: {
    employeeOvertime: emptyState(),
    employeeOvertimes: emptyState(),
    employeeOvertimesByEmployee: emptyState(),
    overtimeAnalytics: emptyState(),
  },
  getters: {
    employeeOvertime: (state: any) => state.employeeOvertime,
    employeeOvertimes: (state: any) => state.employeeOvertimes,
    employeeOvertimesByEmployee: (state: any) => state.employeeOvertimesByEmployee,
    overtimeAnalytics: (state: any) => state.overtimeAnalytics,
  },
  mutations: {
    [SET_EMPLOYEE_OVERTIME]: (state: any, payload: any) => applyMutation(state.employeeOvertime, payload),
    [SET_EMPLOYEE_OVERTIMES]: (state: any, payload: any) => applyMutation(state.employeeOvertimes, payload),
    [SET_EMPLOYEE_OVERTIMES_BY_EMPLOYEE]: (state: any, payload: any) => applyMutation(state.employeeOvertimesByEmployee, payload),
    [SET_OVERTIME_ANALYTICS]: (state: any, payload: any) => applyMutation(state.overtimeAnalytics, payload),
  },
  actions: {
    [GET_EMPLOYEE_OVERTIMES]: async ({ commit, state }: any, payload: any) => {
      const requestId = state.employeeOvertimes.requestId + 1;
      commit(SET_EMPLOYEE_OVERTIMES, { data: null, meta: null, isLoading: true, requestId });

      try {
        const result: AxiosResponse = await EmployeeOvertime.getEmployeeOvertimes(payload as GetEmployeeOvertime);
        if (result?.data?.status) {
          commit(SET_EMPLOYEE_OVERTIMES, { data: result.data.data ?? [], meta: result.data.metaData ?? null, isLoading: false, requestId });
          return true;
        }
        commit(SET_EMPLOYEE_OVERTIMES, { data: [], isLoading: false, requestId });
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_EMPLOYEE_OVERTIMES, { data: [], isLoading: false, requestId });
        return false;
      }
    },

    [GET_EMPLOYEE_OVERTIMES_BY_EMPLOYEE]: async ({ commit, state }: any, payload: any) => {
      const requestId = state.employeeOvertimesByEmployee.requestId + 1;
      commit(SET_EMPLOYEE_OVERTIMES_BY_EMPLOYEE, { data: null, isLoading: true, requestId });

      try {
        const result: AxiosResponse = await EmployeeOvertime.getEmployeeOvertimesByEmployee(payload as GetEmployeeOvertime);
        if (result?.data?.status) {
          commit(SET_EMPLOYEE_OVERTIMES_BY_EMPLOYEE, { data: result.data.data ?? [], isLoading: false, requestId });
          return true;
        }
        commit(SET_EMPLOYEE_OVERTIMES_BY_EMPLOYEE, { data: [], isLoading: false, requestId });
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_EMPLOYEE_OVERTIMES_BY_EMPLOYEE, { data: [], isLoading: false, requestId });
        return false;
      }
    },

    [GET_OVERTIME_ANALYTICS]: async ({ commit, state }: any, payload: any) => {
      const requestId = state.overtimeAnalytics.requestId + 1;
      commit(SET_OVERTIME_ANALYTICS, { data: null, isLoading: true, requestId });

      try {
        const result: AxiosResponse = await EmployeeOvertime.getOvertimeAnalytics(payload as GetOvertimeAnalytics);
        if (result?.data?.status) {
          commit(SET_OVERTIME_ANALYTICS, { data: result.data.data ?? null, isLoading: false, requestId });
          return result.data.data;
        }
        commit(SET_OVERTIME_ANALYTICS, { data: null, isLoading: false, requestId });
        reportError(commit, result);
        return null;
      } catch {
        commit(SET_OVERTIME_ANALYTICS, { data: null, isLoading: false, requestId });
        return null;
      }
    },

    [GET_EMPLOYEE_OVERTIME]: async ({ commit }: any, payload: any) => {
      commit(SET_EMPLOYEE_OVERTIME, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await EmployeeOvertime.getEmployeeOvertimes(payload as GetEmployeeOvertime);
        if (result?.data?.status) {
          commit(SET_EMPLOYEE_OVERTIME, { data: result.data.data?.[0] ?? null, isLoading: false });
          return true;
        }
        commit(SET_EMPLOYEE_OVERTIME, { data: null, isLoading: false });
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_EMPLOYEE_OVERTIME, { data: null, isLoading: false });
        return false;
      }
    },

    [REGISTER_EMPLOYEE_OVERTIME]: async ({ commit }: any, payload: any) => {
      commit(SET_EMPLOYEE_OVERTIME, { isLoading: true });

      try {
        const result: AxiosResponse = await EmployeeOvertime.registerEmployeeOvertime(payload as RegisterEmployeeOvertime);
        commit(SET_EMPLOYEE_OVERTIME, { data: result?.data?.data ?? null, isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_EMPLOYEE_OVERTIME, { isLoading: false });
        return false;
      }
    },

    [EDIT_EMPLOYEE_OVERTIME]: async ({ commit }: any, payload: any) => {
      commit(SET_EMPLOYEE_OVERTIME, { isLoading: true });

      try {
        const result: AxiosResponse = await EmployeeOvertime.editEmployeeOvertime(payload as EditEmployeeOvertime);
        commit(SET_EMPLOYEE_OVERTIME, { data: result?.data?.data ?? null, isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_EMPLOYEE_OVERTIME, { isLoading: false });
        return false;
      }
    },

    [UPDATE_OVERTIME_STATUS]: async ({ commit }: any, payload: any) => {
      commit(SET_EMPLOYEE_OVERTIME, { isLoading: true });

      try {
        const result: AxiosResponse = await EmployeeOvertime.updateOvertimeStatus(payload as UpdateOvertimeStatus);
        commit(SET_EMPLOYEE_OVERTIME, { isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_EMPLOYEE_OVERTIME, { isLoading: false });
        return false;
      }
    },

    [DELETE_EMPLOYEE_OVERTIME]: async ({ commit }: any, payload: string) => {
      commit(SET_EMPLOYEE_OVERTIME, { isLoading: true });

      try {
        const result: AxiosResponse = await EmployeeOvertime.deleteEmployeeOvertime(payload);
        commit(SET_EMPLOYEE_OVERTIME, { isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_EMPLOYEE_OVERTIME, { isLoading: false });
        return false;
      }
    },

    /** Returns the raw response so the caller can turn the blob into a download. */
    [EXPORT_OVERTIME]: async (_ctx: any, payload: any) => {
      try {
        return await EmployeeOvertime.exportOvertime(payload as ExportOvertime);
      } catch {
        return null;
      }
    },

    [CLEAR_EMPLOYEE_OVERTIME_ISLOADING]: ({ commit }: any) => {
      commit(SET_EMPLOYEE_OVERTIME, { isLoading: false });
      commit(SET_EMPLOYEE_OVERTIMES, { isLoading: false });
      commit(SET_EMPLOYEE_OVERTIMES_BY_EMPLOYEE, { isLoading: false });
      commit(SET_OVERTIME_ANALYTICS, { isLoading: false });
    },
  },
};

export default employeeOvertime;
